import { alpha, createTheme } from '@mui/material/styles';

const primaryMainLight = '#1565c0';
const primaryDarkLight = '#0d47a1';
const primarySoftLight = '#e3f2fd';
const appBarGradientEndLight = '#1976d2';
const bgDefaultLight = '#f4f6f9';
const paperLight = '#ffffff';
const borderLight = '#e0e4eb';
const textPrimaryLight = '#1a1d24';
const textSecondaryLight = '#5c6370';

const primaryMainDark = '#64b5f6';
const primaryDarkDark = '#42a5f5';
const primaryLightDark = '#90caf9';
const greyScale = {
  50: '#f8fafc',
  100: '#f1f4f8',
  200: '#e8ecf2',
  300: '#d8dee8',
  400: '#b0b8c4',
  500: '#8a929e',
  600: '#5c6370',
  700: '#454b55',
  800: '#2d323a',
  900: '#1a1d24',
};

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1200,
    xl: 1536,
  },
};

/**
 * @param {'light' | 'dark'} mode
 */
export function createAppTheme(mode) {
  const isDark = mode === 'dark';

  const paletteLight = {
    mode: 'light',
    primary: {
      main: primaryMainLight,
      light: '#42a5f5',
      dark: primaryDarkLight,
      contrastText: '#ffffff',
    },
    background: {
      default: bgDefaultLight,
      paper: paperLight,
    },
    text: {
      primary: textPrimaryLight,
      secondary: textSecondaryLight,
    },
    divider: borderLight,
    action: {
      active: alpha(textPrimaryLight, 0.54),
      hover: alpha(primaryMainLight, 0.06),
      hoverOpacity: 0.06,
      selected: alpha(primaryMainLight, 0.1),
      selectedOpacity: 0.1,
      disabled: alpha(textPrimaryLight, 0.26),
      disabledBackground: alpha(textPrimaryLight, 0.12),
      focus: alpha(primaryMainLight, 0.12),
    },
    grey: greyScale,
  };

  const bgDefaultDark = '#12171f';
  const paperDark = '#1c222c';
  const borderDark = alpha('#ffffff', 0.12);

  const paletteDark = {
    mode: 'dark',
    primary: {
      main: primaryMainDark,
      light: primaryLightDark,
      dark: primaryDarkDark,
      contrastText: '#0d1929',
    },
    background: {
      default: bgDefaultDark,
      paper: paperDark,
    },
    text: {
      primary: '#e8ecf4',
      secondary: alpha('#ffffff', 0.72),
    },
    divider: borderDark,
    action: {
      active: alpha('#ffffff', 0.54),
      hover: alpha(primaryMainDark, 0.12),
      hoverOpacity: 0.12,
      selected: alpha(primaryMainDark, 0.16),
      selectedOpacity: 0.16,
      disabled: alpha('#ffffff', 0.38),
      disabledBackground: alpha('#ffffff', 0.12),
      focus: alpha(primaryMainDark, 0.2),
    },
    grey: greyScale,
  };

  const textSecondaryToken = isDark ? paletteDark.text.secondary : textSecondaryLight;

  const foundation = createTheme({
    breakpoints,
    palette: isDark ? paletteDark : paletteLight,
    typography: {
      fontFamily: '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h5: {
        fontWeight: 500,
        lineHeight: 1.3,
      },
      h6: {
        fontWeight: 500,
        lineHeight: 1.3,
      },
      subtitle2: {
        fontWeight: 500,
        color: textSecondaryToken,
      },
      body1: {
        lineHeight: 1.45,
      },
      body2: {
        lineHeight: 1.45,
        color: textSecondaryToken,
      },
      button: {
        fontWeight: 500,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: 10,
    },
  });

  const p = foundation.palette;
  const bgDefault = p.background.default;
  const paper = p.background.paper;
  const border = p.divider;
  const primaryMain = p.primary.main;
  const primaryDark = p.primary.dark;
  const primaryLight = p.primary.light;
  const appBarGradientEnd = isDark ? primaryLight : appBarGradientEndLight;
  const textPrimary = p.text.primary;
  const primarySoft = isDark ? alpha(primaryMainDark, 0.16) : primarySoftLight;
  const cardShadow = isDark
    ? '0 1px 3px rgba(0, 0, 0, 0.45)'
    : '0 1px 3px rgba(0, 0, 0, 0.08)';

  const shadows = foundation.shadows.slice();
  shadows[0] = 'none';
  shadows[1] = cardShadow;
  shadows[2] = isDark ? '0 2px 6px rgba(0, 0, 0, 0.5)' : '0 2px 6px rgba(0, 0, 0, 0.09)';
  shadows[3] = isDark ? '0 3px 8px rgba(0, 0, 0, 0.55)' : '0 3px 8px rgba(0, 0, 0, 0.1)';
  shadows[4] = isDark ? '0 4px 12px rgba(0, 0, 0, 0.55)' : '0 4px 12px rgba(0, 0, 0, 0.11)';
  shadows[5] = isDark ? '0 6px 16px rgba(0, 0, 0, 0.55)' : '0 6px 16px rgba(0, 0, 0, 0.12)';
  shadows[6] = isDark ? '0 8px 20px rgba(0, 0, 0, 0.55)' : '0 8px 20px rgba(0, 0, 0, 0.13)';

  const alertInfoBg = isDark ? alpha(primaryMainDark, 0.14) : primarySoftLight;
  const alertInfoColor = isDark ? primaryLightDark : primaryDarkLight;

  const scrollTrack = isDark ? bgDefault : greyScale[100];
  const scrollThumb = isDark ? alpha('#ffffff', 0.22) : alpha(textPrimaryLight, 0.18);
  const scrollThumbHover = isDark ? alpha('#ffffff', 0.36) : alpha(textPrimaryLight, 0.32);

  return createTheme(foundation, {
    shadows,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            colorScheme: isDark ? 'dark' : 'light',
          },
          body: {
            backgroundColor: bgDefault,
            color: textPrimary,
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${scrollThumb} ${scrollTrack}`,
          },
          '*::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: scrollTrack,
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: scrollThumb,
            borderRadius: '8px',
            border: `2px solid ${scrollTrack}`,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: scrollThumbHover,
          },
          '*::-webkit-scrollbar-corner': {
            backgroundColor: scrollTrack,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${border}`,
            boxShadow: cardShadow,
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
          color: 'primary',
        },
        styleOverrides: {
          root: isDark
            ? {
                backgroundImage: 'none',
                backgroundColor: paper,
                color: textPrimary,
                borderBottom: `1px solid ${border}`,
                boxShadow: `inset 0 1px 0 ${alpha('#ffffff', 0.06)}, 0 2px 12px ${alpha('#000000', 0.45)}`,
                '& .MuiToolbar-root': {
                  color: textPrimary,
                },
                '& .MuiIconButton-root': {
                  color: alpha(textPrimary, 0.92),
                  '&:hover': {
                    backgroundColor: alpha(primaryMain, 0.12),
                  },
                },
              }
            : {
                background: `linear-gradient(135deg, ${primaryDark} 0%, ${primaryMain} 55%, ${appBarGradientEnd} 100%)`,
                borderBottom: `1px solid ${alpha('#ffffff', 0.12)}`,
              },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: paper,
            borderRight: `1px solid ${border}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            marginLeft: 8,
            marginRight: 8,
            marginTop: 2,
            marginBottom: 2,
            '&.Mui-selected': {
              backgroundColor: primarySoft,
              '&:hover': {
                backgroundColor: alpha(primaryMain, isDark ? 0.22 : 0.14),
              },
            },
            '&.Mui-selected .MuiListItemIcon-root': {
              color: primaryMain,
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: textSecondaryToken,
            minWidth: 40,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            backgroundColor: paper,
          },
          notchedOutline: {
            borderColor: border,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: textSecondaryToken,
            '&.Mui-focused': {
              color: primaryMain,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
          },
          containedPrimary: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: cardShadow,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            color: textSecondaryToken,
            backgroundColor: alpha(textPrimary, isDark ? 0.06 : 0.03),
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardInfo: {
            backgroundColor: alertInfoBg,
            color: alertInfoColor,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            border: `1px solid ${border}`,
            boxShadow: shadows[6],
          },
        },
      },
    },
  });
}
